import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApAgingResponse,
  ApBillDuplicateResponse,
  ApBillListRow,
  ApBillStatus,
  ApBillWithLines,
  ApCreditDebitNoteListRow,
  ApCreditDebitNoteWithLines,
  ApDashboard,
  ApNoteStatus,
  ApPaymentRun,
  ApPaymentRunWithDetail,
  ApPaymentWithAllocations,
  ApPaymentWithDetail,
  CreateApBill,
  CreateApCreditDebitNote,
  CreateApPayment,
  CreateApPaymentRun,
  CreditDebitType,
  LinkOpContract,
  RealizeApBillVat,
  RejectApBill,
  VendorStatement,
} from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as AR / branches / customers).
const billsKey = (companyId: string | undefined) => ['ap-bills', companyId] as const;
const paymentsKey = (companyId: string | undefined) => ['ap-payments', companyId] as const;
const agingKey = (companyId: string | undefined) => ['ap-aging', companyId] as const;
// AP Phase C2 — credit/debit notes (ใบลด/เพิ่มหนี้) cache scope.
const notesKey = (companyId: string | undefined) => ['ap-credit-debit-notes', companyId] as const;

export interface ApBillFilters {
  status?: ApBillStatus;
  vendorId?: string;
  branchId?: string;
  departmentId?: string;
  dateFrom?: string;
  dateTo?: string;
  overdue?: boolean;
}

export function useApBills(companyId: string | undefined, filters: ApBillFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.vendorId) qs.set('vendorId', filters.vendorId);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  if (filters.overdue) qs.set('overdue', 'true');
  const search = qs.toString();
  return useQuery({
    queryKey: [...billsKey(companyId), filters],
    queryFn: () =>
      api<ApBillListRow[]>(`/companies/${companyId}/ap/bills${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useApBill(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ap-bills', companyId, id],
    queryFn: () => api<ApBillWithLines>(`/companies/${companyId}/ap/bills/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateApBill(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateApBill; idempotencyKey?: string }) =>
      api<ApBillWithLines>(`/companies/${companyId}/ap/bills`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billsKey(companyId) });
      void qc.invalidateQueries({ queryKey: agingKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// Approve a PENDING bill — flips status to POSTED + creates the deferred JV.
// Invalidates bills (status changed) + journal-entries (new JV) + aging
// (new POSTED bill enters the aging report) + dashboard (KPIs shift).
export function useApproveApBill(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ApBillWithLines>('POST', `/companies/${companyId}/ap/bills/${id}/approve`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billsKey(companyId) });
      void qc.invalidateQueries({ queryKey: agingKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
    },
  });
}

// Reject a PENDING bill — flips status to VOID with the reason stamped.
// No JV side-effect, so journal-entries cache is left alone.
export function useRejectApBill(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiJson<RejectApBill, ApBillWithLines>(
        'POST',
        `/companies/${companyId}/ap/bills/${id}/reject`,
        { reason },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billsKey(companyId) });
    },
  });
}

// ============================================================
// M5b true-up — link / unlink a PENDING bill to an op-contract standing accrual.
// Linking marks the target accrual as trued-up (it drops out of the linkable
// picker + can no longer be reversed), so besides the ap-bills list we also
// invalidate the op-contract accrual caches (the picker source) + the contract
// list/detail. Cache keys are referenced by their string prefix to avoid an
// import cycle with op-contracts.ts (same scoping the op-contracts module uses).
// ============================================================

function invalidateOpContractTrueUp(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: billsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['op-contract-accruals', companyId] });
  void qc.invalidateQueries({ queryKey: ['op-contracts', companyId] });
}

// Link a PENDING bill to an op-contract POSTED accrual (true-up). On post the
// bill's JV clears the accrual (Dr ACCRUED_EXPENSE) and books only the variance.
export function useLinkBillToOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LinkOpContract }) =>
      apiJson<LinkOpContract, ApBillWithLines>(
        'POST',
        `/companies/${companyId}/ap/bills/${id}/link-opcontract`,
        data,
      ),
    onSuccess: () => invalidateOpContractTrueUp(qc, companyId),
  });
}

// Clear an AP bill's op-contract true-up link (PENDING bills only). Frees the
// accrual back into the linkable picker, so we invalidate the same caches.
export function useUnlinkBillFromOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ApBillWithLines>(
        'POST',
        `/companies/${companyId}/ap/bills/${id}/unlink-opcontract`,
      ),
    onSuccess: () => invalidateOpContractTrueUp(qc, companyId),
  });
}

// ============================================================
// Central-engine approval hooks (M1b-ii) — mirror the journal approval hooks.
// Each invalidates ap-bills (status/approvalStatus changed) + journal-entries
// (final-step approval posts the AP JV) + dashboard (KPIs shift). The engine
// verbs all resolve to the refreshed ApBillWithLines via the service's findBill.
// ============================================================

function invalidateApEngine(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: billsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

// Submit a DRAFT / RETURNED bill into the engine chain.
export function useSubmitApBill(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ApBillWithLines>('POST', `/companies/${companyId}/ap/bills/${id}/submit`),
    onSuccess: () => invalidateApEngine(qc, companyId),
  });
}

// Approve the current engine step (final-step approval auto-posts the AP JV).
export function useApproveStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, ApBillWithLines>(
        'POST',
        `/companies/${companyId}/ap/bills/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateApEngine(qc, companyId),
  });
}

// Reject the approval at a step (terminal — accounting status → VOID).
export function useRejectStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ApBillWithLines>(
        'POST',
        `/companies/${companyId}/ap/bills/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateApEngine(qc, companyId),
  });
}

// Return a PENDING bill for revision (RETURNED — resubmittable).
export function useReturnApBill(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ApBillWithLines>(
        'POST',
        `/companies/${companyId}/ap/bills/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateApEngine(qc, companyId),
  });
}

export interface ApPaymentFilters {
  vendorId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useApPayments(companyId: string | undefined, filters: ApPaymentFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.vendorId) qs.set('vendorId', filters.vendorId);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  const search = qs.toString();
  return useQuery({
    queryKey: [...paymentsKey(companyId), filters],
    queryFn: () =>
      api<ApPaymentWithAllocations[]>(
        `/companies/${companyId}/ap/payments${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useCreateApPayment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateApPayment; idempotencyKey?: string }) =>
      api<ApPaymentWithAllocations>(`/companies/${companyId}/ap/payments`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: paymentsKey(companyId) });
      void qc.invalidateQueries({ queryKey: billsKey(companyId) });
      void qc.invalidateQueries({ queryKey: agingKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// M7 Phase 1b — single-bill payment engine 4-eyes (docType APV).
// createPayment now returns a DRAFT (no JV); the maker submits it into the
// central approval engine; the finance approver pool decides. On FINAL approve
// the server posts the Dr AP_TRADE / Cr cash JV (journalEntryId set). A payment
// mutation therefore touches payments + bills (paidAmount) + aging + the journal
// list + dashboard KPIs — invalidate all of them.
// ============================================================
function invalidatePayment(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: paymentsKey(companyId) });
  void qc.invalidateQueries({ queryKey: billsKey(companyId) });
  void qc.invalidateQueries({ queryKey: agingKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

export function useApPayment(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ap-payment', companyId, id],
    queryFn: () => api<ApPaymentWithDetail>(`/companies/${companyId}/ap/payments/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useSubmitApPayment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ApPaymentWithDetail>(
        'POST',
        `/companies/${companyId}/ap/payments/${id}/submit`,
      ),
    onSuccess: () => invalidatePayment(qc, companyId),
  });
}

export function useApproveApPaymentStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, ApPaymentWithDetail>(
        'POST',
        `/companies/${companyId}/ap/payments/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePayment(qc, companyId),
  });
}

export function useRejectApPayment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ApPaymentWithDetail>(
        'POST',
        `/companies/${companyId}/ap/payments/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePayment(qc, companyId),
  });
}

export function useReturnApPayment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ApPaymentWithDetail>(
        'POST',
        `/companies/${companyId}/ap/payments/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidatePayment(qc, companyId),
  });
}

export function useApAging(companyId: string | undefined, asOfDate?: string) {
  const qs = new URLSearchParams();
  if (asOfDate) qs.set('asOfDate', asOfDate);
  const search = qs.toString();
  return useQuery({
    queryKey: [...agingKey(companyId), { asOfDate: asOfDate ?? null }],
    queryFn: () =>
      api<ApAgingResponse>(`/companies/${companyId}/ap/aging${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

// ============================================================
// Duplicate-check hook (Phase A5) — read-only entry-time surfacing.
// Only fires once both vendorId and invoiceNo are non-empty. The hard
// guard remains the server-side P2002→409 in createBill.
// ============================================================
export function useApBillCheckDuplicate(
  companyId: string | undefined,
  params: { vendorId?: string; invoiceNo?: string },
) {
  const enabled =
    !!companyId && !!params.vendorId && !!params.invoiceNo && params.invoiceNo.length > 0;
  const qs = new URLSearchParams();
  if (params.vendorId) qs.set('vendorId', params.vendorId);
  if (params.invoiceNo) qs.set('invoiceNo', params.invoiceNo);
  return useQuery({
    queryKey: ['ap-check-duplicate', companyId, params.vendorId ?? null, params.invoiceNo ?? null],
    queryFn: () =>
      api<ApBillDuplicateResponse>(
        `/companies/${companyId}/ap/bills/check-duplicate?${qs.toString()}`,
      ),
    enabled,
  });
}

// ============================================================
// B3: Deferred input-VAT hooks
// useSuspenseBills — POSTED bills whose vatCreditStatus is SUSPENSE (awaiting
//   original tax invoice). Filters client-side from the existing list endpoint
//   so no new API route is needed.
// useRealizeApBillVat — PATCH realize-vat, flips SUSPENSE → CLAIMABLE.
// ============================================================

export function useSuspenseBills(companyId: string | undefined) {
  return useQuery({
    queryKey: ['ap-bills', companyId, 'suspense'],
    queryFn: async () => {
      const rows = await api<ApBillListRow[]>(`/companies/${companyId}/ap/bills?status=POSTED`);
      return rows.filter((b) => b.vatCreditStatus === 'SUSPENSE');
    },
    enabled: !!companyId,
  });
}

export function useRealizeApBillVat(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RealizeApBillVat }) =>
      apiJson<RealizeApBillVat, ApBillWithLines>(
        'PATCH',
        `/companies/${companyId}/ap/bills/${id}/realize-vat`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Cross-vendor payment-run hooks (B2) — engine-approved batch
// ============================================================

const runsKey = (companyId: string | undefined) => ['ap-payment-runs', companyId] as const;

export function useApPaymentRuns(companyId: string | undefined) {
  return useQuery({
    queryKey: runsKey(companyId),
    queryFn: () => api<ApPaymentRun[]>(`/companies/${companyId}/ap/payment-runs`),
    enabled: !!companyId,
  });
}

export function useApPaymentRun(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ap-payment-run', companyId, id],
    queryFn: () => api<ApPaymentRunWithDetail>(`/companies/${companyId}/ap/payment-runs/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Finalizing a run spawns per-vendor payments + posts JVs, so a run mutation
// touches runs + payments + bills (paidAmount) + aging + the journal list.
function invalidateRun(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: runsKey(companyId) });
  void qc.invalidateQueries({ queryKey: paymentsKey(companyId) });
  void qc.invalidateQueries({ queryKey: billsKey(companyId) });
  void qc.invalidateQueries({ queryKey: agingKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

export function useCreateApPaymentRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateApPaymentRun) =>
      api<ApPaymentRunWithDetail>(`/companies/${companyId}/ap/payment-runs`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidateRun(qc, companyId),
  });
}

export function useSubmitApPaymentRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ApPaymentRunWithDetail>(
        'POST',
        `/companies/${companyId}/ap/payment-runs/${id}/submit`,
      ),
    onSuccess: () => invalidateRun(qc, companyId),
  });
}

export function useApproveRunStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; comment?: string; sodOverrideReason?: string },
        ApPaymentRunWithDetail
      >(
        'POST',
        `/companies/${companyId}/ap/payment-runs/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateRun(qc, companyId),
  });
}

export function useRejectRunStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ApPaymentRunWithDetail>(
        'POST',
        `/companies/${companyId}/ap/payment-runs/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateRun(qc, companyId),
  });
}

export function useReturnRunStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ApPaymentRunWithDetail>(
        'POST',
        `/companies/${companyId}/ap/payment-runs/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateRun(qc, companyId),
  });
}

// ============================================================
// AP Credit/Debit Note hooks (ใบลดหนี้ / ใบเพิ่มหนี้) — AP Phase C2.
//
// Lifecycle: create DRAFT (no JV) → submit into the central approval engine →
// approve/reject/return steps. Final-step approval auto-posts the note JV and
// bumps the source bill's noteAdjustment (CREDIT −total / DEBIT +total), moving
// its outstanding. The engine hooks mirror the ap-bill / payment-run engine
// hooks exactly (doc-type-agnostic { step, comment|reason }). Each mutation
// invalidates the notes key AND ap-bills (the bill's noteAdjustment/outstanding
// changed) + aging + journal-entries + dashboard.
// ============================================================

export interface ApCreditDebitNoteFilters {
  status?: ApNoteStatus;
  billId?: string;
  noteType?: CreditDebitType;
}

export function useApCreditDebitNotes(
  companyId: string | undefined,
  filters: ApCreditDebitNoteFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.billId) qs.set('billId', filters.billId);
  if (filters.noteType) qs.set('noteType', filters.noteType);
  const search = qs.toString();
  return useQuery({
    queryKey: [...notesKey(companyId), filters],
    queryFn: () =>
      api<ApCreditDebitNoteListRow[]>(
        `/companies/${companyId}/ap/credit-debit-notes${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useApCreditDebitNote(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ap-credit-debit-note', companyId, id],
    queryFn: () =>
      api<ApCreditDebitNoteWithLines>(`/companies/${companyId}/ap/credit-debit-notes/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Create a DRAFT credit/debit note — no JV (the note JV books on final approval).
export function useCreateApCreditDebitNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreateApCreditDebitNote;
      idempotencyKey?: string;
    }) =>
      api<ApCreditDebitNoteWithLines>(`/companies/${companyId}/ap/credit-debit-notes`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKey(companyId) });
      void qc.invalidateQueries({ queryKey: billsKey(companyId) });
    },
  });
}

// Engine-flow invalidation: notes (status/approvalStatus) + bills & aging (a
// POSTED note moves the source bill's noteAdjustment → outstanding) + the
// journal-entries cache (final approval books the note JV) + dashboard.
function invalidateApNoteEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: notesKey(companyId) });
  void qc.invalidateQueries({ queryKey: billsKey(companyId) });
  void qc.invalidateQueries({ queryKey: agingKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

// Submit a DRAFT / RETURNED note into the engine chain.
export function useSubmitApNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ApCreditDebitNoteWithLines>(
        'POST',
        `/companies/${companyId}/ap/credit-debit-notes/${id}/submit`,
      ),
    onSuccess: () => invalidateApNoteEngine(qc, companyId),
  });
}

// Approve the current engine step (final-step approval auto-posts the note JV).
export function useApproveApNoteStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; comment?: string; sodOverrideReason?: string },
        ApCreditDebitNoteWithLines
      >(
        'POST',
        `/companies/${companyId}/ap/credit-debit-notes/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateApNoteEngine(qc, companyId),
  });
}

// Reject the approval at a step (terminal — accounting status → VOID).
export function useRejectApNoteStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; reason: string; sodOverrideReason?: string },
        ApCreditDebitNoteWithLines
      >(
        'POST',
        `/companies/${companyId}/ap/credit-debit-notes/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateApNoteEngine(qc, companyId),
  });
}

// Return a PENDING note for revision (RETURNED — resubmittable).
export function useReturnApNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; reason: string; sodOverrideReason?: string },
        ApCreditDebitNoteWithLines
      >(
        'POST',
        `/companies/${companyId}/ap/credit-debit-notes/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateApNoteEngine(qc, companyId),
  });
}

// ============================================================
// Vendor 360 / statement (AP Phase C5) — a read-only per-vendor aggregation
// (bills + payments + aging + 50-ทวิ certs + credit/debit notes + KPIs). The
// Vendor-360 page mounts this; `asOf` drives the aging/overdue as-of date and
// defaults to today server-side when omitted.
// ============================================================
export function useVendorStatement(
  companyId: string | undefined,
  vendorId: string | undefined,
  asOf?: string,
) {
  const qs = new URLSearchParams();
  if (asOf) qs.set('asOf', asOf);
  const search = qs.toString();
  return useQuery({
    queryKey: ['ap-vendor-statement', companyId, vendorId, { asOf: asOf ?? null }],
    queryFn: () =>
      api<VendorStatement>(
        `/companies/${companyId}/ap/vendors/${vendorId}/statement${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId && !!vendorId,
  });
}

// ============================================================
// AP KPI dashboard (AP Phase C7a) — a READ-ONLY company-wide aggregation over
// the AP module's bills + payments (headline + process KPIs + aging + top
// vendors + a 6-month trend). The /ap แดชบอร์ด tab mounts this; `asOf` drives
// the aging/overdue as-of date and defaults to today server-side when omitted.
// ============================================================
export function useApDashboard(companyId: string | undefined, asOf?: string) {
  const qs = new URLSearchParams();
  if (asOf) qs.set('asOf', asOf);
  const search = qs.toString();
  return useQuery({
    queryKey: ['ap-dashboard', companyId, { asOf: asOf ?? null }],
    queryFn: () =>
      api<ApDashboard>(`/companies/${companyId}/ap/dashboard${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}
